import React, { ChangeEvent, useEffect, useState } from "react";
import { User, Role, Organization } from "../common/ObjectTypes";
import { toast } from "react-toastify";
import { MoonLoader } from "react-spinners";
import AddEditUser from "./AddEditUser";
import { useNavigate, useParams } from "react-router-dom";
import {
  createUser,
  getUserById,
  updateUser,
  updateUserOrgs,
  updateUserRoles,
  updateUserPassword,
} from "../../api/users/usersApi";
import { getRoles } from "../../api/roles/rolesApi";
import { getOrganizations } from "../../api/organizations/organizationsApi";
import { getCurrentUser } from "../../helpers/authHeader";

type ManageAddEditUserProps = {
  existingUser: boolean;
};

const ManageAddEditUser = ({ existingUser }: ManageAddEditUserProps) => {
  const { v4: uuidv4 } = require("uuid");
  const currentUser = getCurrentUser();
  const [pageLoaded, setPageLoaded] = useState(false);
  const navigate = useNavigate();
  const [addUser, setAddUser] = useState(false);
  const { id: userId } = useParams<{ id: string }>(); // Extract the `id` parameter
  const [errors, setErrors] = useState<any>({});
  const [user, setUser] = useState<User>({
    userId: "",
    firstName: "",
    lastName: "",
    userName: "",
    email: "",
    roles: [],
    organizations: [],
    isHeadsetAdmin: false,
    isSystemAdmin: false,
  });
  const [roles, setRoles] = useState<Role[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      try {
        if (existingUser) {
          let temp = await getUserById(userId || "");
          setUser(temp);
        }
        loadRoles();
        loadOrgs();
        setPageLoaded(true);
      } catch (err) {
        console.log(err);
      }
    };

    const loadRoles = async () => {
      try {
        const temp = await getRoles();
        const filteredRoles = temp.filter(
          (role: any) => role.name !== "HeadsetAdmin"
        );
        setRoles(filteredRoles);
      } catch (err) {
        console.log(err);
      }
    };

    const loadOrgs = async () => {
      try {
        const temp = await getOrganizations();
        setOrganizations(temp);
      } catch (err) {
        console.log(err);
      }
    };

    if (!pageLoaded) {
      loadUser();
    }
  }, [pageLoaded, existingUser, userId]);

  const handleSelectUser = (user: User) => {
    setUser({
      userId: user.userId,
      firstName: user.firstName,
      lastName: user.lastName,
      userName: user.userName,
      email: user.email,
      roles: user.roles,
      organizations: user.organizations,
      isHeadsetAdmin: user.isHeadsetAdmin,
      isSystemAdmin: user.isSystemAdmin,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setUser((prev: User) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeSingleSelect = (name: string, e: any) => {
    const newValue = e === null ? null : e.value;
    setUser((prevUser: any) => ({
      ...prevUser,
      [name]: newValue,
    }));
  };

  const handleChangeRoles = (selectedOption: any) => {
    const role = selectedOption
      ? {
          roleId: selectedOption.value,
          name: selectedOption.label,
        }
      : null;

    const includesHeadsetAdmin = role && role.name === "HeadsetAdmin";
    const includesSystemAdmin = role && role.name === "SystemAdmin";

    setUser((prevUser: any) => ({
      ...prevUser,
      roles: role ? [role] : [], // Wrap in an array for consistency
      isHeadsetAdmin: includesHeadsetAdmin,
      isSystemAdmin: includesSystemAdmin,
    }));
  };

  const handleChangeOrgs = (selectedOption: any) => {
    const org = selectedOption
      ? {
          orgId: selectedOption.value,
          name: selectedOption.label,
        }
      : null;

    setUser((prevUser: any) => ({
      ...prevUser,
      organizations: org ? [org] : [], // Wrap in an array for consistency
    }));
  };

  const formIsValid = (): boolean => {
    const newErrors: any = {};

    if (currentUser.isSystemAdmin) {
      const orgAdminRole = user.roles.find(
        (role: any) => role.name === "OrgAdmin"
      );

      if (orgAdminRole && user.organizations.length === 0) {
        newErrors.organizations =
          "Organization selection is required for organization admins.";
      }
    }

    if (!user.firstName) {
      newErrors.firstName = "First name is required";
    }
    if (!user.lastName) {
      newErrors.lastName = "Last name is required";
    }
    if (!user.userName) {
      newErrors.userName = "Username is required";
    }
    if (!user.email) {
      newErrors.email = "Email is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e: any) => {
    e.preventDefault();

    if (user.roles.length === 0 && !currentUser.isSystemAdmin) {
      const orgAdminRole = roles.find((role: any) => role.name === "OrgAdmin");
      if (orgAdminRole) {
        user.roles = [
          {
            roleId: orgAdminRole.roleId,
            name: orgAdminRole.name,
          },
        ];
      }
    }

    if (
      user.organizations.length === 0 &&
      !currentUser.isSystemAdmin &&
      currentUser.organizations &&
      currentUser.organizations.length > 0
    ) {
      user.organizations = [
        {
          organizationId: currentUser.organizations[0].organizationId,
          name: currentUser.organizations[0].name,
        },
      ];
    }

    const roleId = user.roles.map((role: any) => ({
      id: role.roleId || role.id,
      name: role.name,
    }));

    const organizationId = user.organizations.map((org: any) => ({
      organizationId: org.orgId || org.organizationId,
      name: org.name,
    }));

    const updatedUser = {
      userId: user.userId || uuidv4(),
      firstName: user.firstName,
      lastName: user.lastName,
      userName: user.userName,
      email: user.email,
      isSystemAdmin: user.isSystemAdmin,
      isHeadSetAdmin: user.isHeadsetAdmin,
      roles: roleId,
      organizations: organizationId,
    };
    try {
      if (!formIsValid()) return;
      if (!existingUser) {
        await createUser(updatedUser);
      } else {
        await updateUser(updatedUser);
      }

      toast.success(
        user.firstName +
          user.lastName +
          (existingUser ? " Updated" : " Added."),
        {
          position: toast.POSITION.TOP_RIGHT,
          hideProgressBar: true,
          autoClose: 2500,
        }
      );
      navigate("/users");
      setAddUser(false);
      setPageLoaded(false);
    } catch (error: any) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setErrors({ onSave: error.response.data.message });
      } else {
        setErrors({ onSave: "An unexpected error occurred." });
      }
    }
  };

  const handleUpdatePassword = async (e: any) => {
    e.preventDefault();
    try {
      await updateUserPassword(user.userId, user.password ? user.password : "");
      toast.success("Password was updated.", {
        position: toast.POSITION.TOP_RIGHT,
        hideProgressBar: true,
        autoClose: 2500,
      });
      setUser((prevUser) => ({
        ...prevUser,
        password: "",
      }));
      setModalOpen(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancelEdit = () => {
    setUser({
      userId: "",
      firstName: "",
      lastName: "",
      userName: "",
      email: "",
      roles: [],
      organizations: [],
      isHeadsetAdmin: false,
      isSystemAdmin: false,
    });
    navigate("/users");
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setUser((prevUser) => ({
      ...prevUser,
      password: "", // Reset the password field when closing modal
    }));
    setModalOpen(false);
  };

  // const handleDelete = async () => {
  //   try {
  //     await deleteSpecies(species.speciesId ? species.speciesId : "");
  //     setSpecies({
  //       name: "",
  //       speciesId: "",
  //       colloquialName: "",
  //       scientificName: "",
  //       description: "",
  //       populationTrendId: "",
  //       iucnStatusId: "",
  //       speciesCategoryId: "",
  //       rowVersion: "",
  //     });
  //     toast.success("Species was deleted.", {
  //       position: toast.POSITION.TOP_RIGHT,
  //       hideProgressBar: true,
  //       autoClose: 2500,
  //     });
  //     setPageLoaded(false);
  //   } catch (error) {
  //     console.log(error);
  //   }
  // };
  return !pageLoaded ? (
    <div className="flex justify-center items-center">
      <MoonLoader color="#9EB45B" />
    </div>
  ) : (
    <div className="">
      <AddEditUser
        user={user}
        roles={roles}
        orgs={organizations}
        errors={errors}
        addUser={addUser}
        onSelectUser={handleSelectUser}
        onCancelEdit={handleCancelEdit}
        onChange={handleChange}
        onSave={handleSave}
        onChangeSingleSelect={handleChangeSingleSelect}
        onChangeRoles={handleChangeRoles}
        onChangeOrgs={handleChangeOrgs}
        onUpdatePassword={handleUpdatePassword}
        existingUser={existingUser}
        onOpenPasswordModal={handleOpenModal}
        onClosePasswordModal={handleCloseModal}
        isModalOpen={isModalOpen}
      />
    </div>
  );
};

export default ManageAddEditUser;
